import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Avatar, Box, Button, IconButton, Typography } from '@mui/material';
import { bool, object, string } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getActiveVoiceMessageId,
  setActiveVoiceMessageId,
  subscribeActiveVoiceMessage,
} from 'utils/activeVoiceMessage';
import {
  getVoiceMessagePlaybackRateIndex,
  setVoiceMessagePlaybackRateIndex,
  subscribeVoiceMessagePlaybackRateIndex,
  VOICE_MESSAGE_PLAYBACK_RATES,
} from 'utils/voiceMessagePlaybackRatePreference';
import {
  peaksFromAttachmentMeta,
  VOICE_MESSAGE_WAVEFORM_STORAGE_SAMPLES,
  waveformHeightsForDisplay,
} from 'utils/voiceMessageWaveformPeaks';
import { MESSAGE_BUBBLE_MAX_WIDTH_PX } from '../messageBubbleMaxWidth';
import VoiceMessageWaveform from './VoiceMessageWaveform';

const SPEED_PILL_WIDTH = 36;
const PLAY_CONTROL_WIDTH = 36;
const ROW_GAP = 10;
const WAVEFORM_TIME_INDENT = SPEED_PILL_WIDTH + ROW_GAP + PLAY_CONTROL_WIDTH + ROW_GAP;

function formatDuration(seconds) {
  if (!seconds || !Number.isFinite(seconds)) return '00:00';
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(Math.floor(seconds % 60)).padStart(2, '0');
  return `${m}:${s}`;
}

/** Backend meta.duration often "00:00:09" or "00:09" */
function durationSecondsFromMeta(meta) {
  const raw = meta?.duration;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const parts = raw
    .trim()
    .split(':')
    .map(p => Number.parseInt(p, 10));
  if (parts.some(Number.isNaN)) return null;
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return null;
}

/** Flat peaks when API has no `meta.waveformData` (legacy uploads) — still render bars, not a fragile line. */
const LEGACY_VOICE_PLACEHOLDER_PEAKS = Array.from(
  { length: VOICE_MESSAGE_WAVEFORM_STORAGE_SAMPLES },
  () => 0.14,
);

const VoiceMessageBubble = ({ data, senderName = '', senderAvatar = null }) => {
  const audioRef = useRef(null);
  const playingRef = useRef(false);
  const activeMessageKeyRef = useRef(null);

  const activeMessageKey = data._id ?? data.httpLink ?? null;
  activeMessageKeyRef.current = activeMessageKey;

  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackEngagedOnce, setPlaybackEngagedOnce] = useState(false);
  const [rateIndex, setRateIndex] = useState(() => getVoiceMessagePlaybackRateIndex());
  const [scrubProgress, setScrubProgress] = useState(null);

  const rate = VOICE_MESSAGE_PLAYBACK_RATES[rateIndex];
  const progress = duration > 0 ? currentTime / duration : 0;
  const rawDisplayProgress = scrubProgress !== null ? scrubProgress : progress;
  const displayProgress = Number.isFinite(rawDisplayProgress)
    ? Math.max(0, Math.min(1, rawDisplayProgress))
    : 0;

  const metaPeaks = peaksFromAttachmentMeta(data.meta);
  const storedPeaks = metaPeaks ?? LEGACY_VOICE_PLACEHOLDER_PEAKS;
  const waveformHeights = waveformHeightsForDisplay(storedPeaks);
  const variant = 'bars';

  const displayTime =
    playing || currentTime > 0 ? formatDuration(currentTime) : formatDuration(duration);

  playingRef.current = playing;

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setPlaybackEngagedOnce(false);
    setScrubProgress(null);
    const fromMeta = durationSecondsFromMeta(data.meta);
    setDuration(fromMeta != null && fromMeta > 0 ? fromMeta : 0);
  }, [data._id, data.httpLink, data.mimeType, data.name, data.meta]);

  useEffect(() => {
    return subscribeVoiceMessagePlaybackRateIndex(() => {
      setRateIndex(getVoiceMessagePlaybackRateIndex());
    });
  }, []);

  useEffect(() => {
    const unsub = subscribeActiveVoiceMessage(activeId => {
      if (activeId !== activeMessageKeyRef.current) {
        const el = audioRef.current;
        if (el && !el.paused) {
          el.pause();
        }
        setPlaying(false);
        setPlaybackEngagedOnce(false);
      }
    });
    return unsub;
  }, []);

  useEffect(
    () => () => {
      if (getActiveVoiceMessageId() === activeMessageKeyRef.current) {
        setActiveVoiceMessageId(null);
        audioRef.current?.pause();
      }
    },
    [],
  );

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = rate;
  }, [rate]);

  useEffect(() => {
    const el = audioRef.current;
    if (el && 'preservesPitch' in el) {
      el.preservesPitch = true;
    }
  }, [data._id, data.httpLink]);

  const applyDurationFromEvent = useCallback(
    target => {
      const d = target.duration;
      const fromEl = d === Infinity || !Number.isFinite(d) ? 0 : d;
      if (fromEl > 0) {
        setDuration(fromEl);
      } else {
        const fromMeta = durationSecondsFromMeta(data.meta);
        if (fromMeta != null && fromMeta > 0) {
          setDuration(fromMeta);
        }
      }
    },
    [data.meta],
  );

  const handleLoadedMetadata = useCallback(
    e => {
      applyDurationFromEvent(e.target);
    },
    [applyDurationFromEvent],
  );

  const handleTimeUpdate = useCallback(e => {
    if (!playingRef.current) {
      setCurrentTime(e.target.currentTime);
    }
  }, []);

  /** `timeupdate` is sparse (~4Hz); rAF keeps scrubber motion smooth while playing. */
  useEffect(() => {
    if (!playing) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;
    let raf = 0;
    const tick = () => {
      setCurrentTime(el.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const handleEnded = useCallback(() => {
    setPlaying(false);
    setCurrentTime(0);
    setPlaybackEngagedOnce(false);
    setActiveVoiceMessageId(null);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, []);

  const openAudioExternally = useCallback(() => {
    if (data.httpLink) {
      window.open(data.httpLink, '_blank', 'noopener,noreferrer');
    }
  }, [data.httpLink]);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el || !data.httpLink) return;

    if (playing) {
      el.pause();
      setPlaying(false);
      setCurrentTime(el.currentTime);
      return;
    }

    if (activeMessageKey != null) {
      setActiveVoiceMessageId(activeMessageKey);
    }
    if (!playbackEngagedOnce) setPlaybackEngagedOnce(true);
    el.playbackRate = rate;
    if ('preservesPitch' in el) {
      el.preservesPitch = true;
    }

    const p = el.play();
    if (p !== undefined) {
      p.then(() => setPlaying(true)).catch(() => openAudioExternally());
    } else {
      setPlaying(true);
    }
  }, [activeMessageKey, data.httpLink, openAudioExternally, playbackEngagedOnce, playing, rate]);

  const cycleRate = useCallback(() => {
    const next = (getVoiceMessagePlaybackRateIndex() + 1) % VOICE_MESSAGE_PLAYBACK_RATES.length;
    setVoiceMessagePlaybackRateIndex(next);
  }, []);

  const handleSeek = useCallback(
    p => {
      if (audioRef.current && duration > 0) {
        audioRef.current.currentTime = p * duration;
        setCurrentTime(p * duration);
      }
      setScrubProgress(p);
    },
    [duration],
  );

  const handleScrubStart = useCallback(() => {
    setScrubProgress(progress);
  }, [progress]);

  const handleScrubEnd = useCallback(() => {
    setScrubProgress(null);
  }, []);

  const rateLabel = rate === 1 ? '1x' : rate === 1.5 ? '1.5x' : '2x';

  return (
    <Box
      sx={theme => ({
        width: MESSAGE_BUBBLE_MAX_WIDTH_PX,
        maxWidth: '100%',
        boxSizing: 'border-box',
        padding: theme.spacing(1, 1.25),
      })}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- voice message playback, no captions */}
      <audio
        key={data._id || data.httpLink}
        ref={audioRef}
        src={data.httpLink || undefined}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        preload="auto"
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: `${ROW_GAP}px` }}>
        <Box
          sx={{
            width: SPEED_PILL_WIDTH,
            height: SPEED_PILL_WIDTH,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          {playbackEngagedOnce ? (
            <Button
              variant="text"
              size="small"
              onClick={cycleRate}
              sx={theme => ({
                minWidth: SPEED_PILL_WIDTH,
                minHeight: 22,
                height: 22,
                borderRadius: '11px',
                fontSize: 11,
                lineHeight: 1,
                fontWeight: 600,
                textTransform: 'none',
                padding: theme.spacing(0, 1),
                backgroundColor: theme.palette.grey[700],
                color: theme.palette.common.white,
                '&:hover': {
                  backgroundColor: theme.palette.grey[800],
                },
                whiteSpace: 'nowrap',
              })}
            >
              {rateLabel}
            </Button>
          ) : (
            <Box sx={{ position: 'relative', width: SPEED_PILL_WIDTH, height: SPEED_PILL_WIDTH }}>
              <Avatar
                sx={{ width: SPEED_PILL_WIDTH, height: SPEED_PILL_WIDTH }}
                src={senderAvatar}
                alt={senderName}
              >
                {senderName?.[0]}
              </Avatar>
              <Box
                sx={theme => ({
                  position: 'absolute',
                  bottom: '-2px',
                  right: '-2px',
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                })}
              >
                <MicIcon style={{ fontSize: 12 }} />
              </Box>
            </Box>
          )}
        </Box>

        <IconButton
          type="button"
          onClick={togglePlay}
          disabled={!data.httpLink}
          size="small"
          sx={theme => ({
            width: PLAY_CONTROL_WIDTH,
            height: PLAY_CONTROL_WIDTH,
            flexShrink: 0,
            padding: theme.spacing(0.5),
            backgroundColor: 'transparent',
            color:
              theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[800],
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          })}
        >
          {playing ? <PauseIcon sx={{ fontSize: 22 }} /> : <PlayArrowIcon sx={{ fontSize: 22 }} />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0, marginRight: '6px' }}>
          <VoiceMessageWaveform
            variant={variant}
            heights={waveformHeights}
            progress={displayProgress}
            onSeekProgress={handleSeek}
            onScrubStart={handleScrubStart}
            onScrubEnd={handleScrubEnd}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', paddingLeft: `${WAVEFORM_TIME_INDENT}px` }}>
        <Typography
          variant="caption"
          sx={{ fontSize: 11, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
        >
          {displayTime}
        </Typography>
      </Box>
    </Box>
  );
};

VoiceMessageBubble.propTypes = {
  data: object.isRequired,
  right: bool,
  senderName: string,
  senderAvatar: string,
};

export default VoiceMessageBubble;
